import type { CardFields, CardMetadata, CardUrl } from '@blinq-types';
import { isURLFieldValueValid } from '@modules/card-helpers';

import { type CardFlavour, CardFlavours } from './types';

export function shouldShowTextField(value: string | undefined, isBeingEdited: boolean): boolean {
  if (isBeingEdited) return true;
  else return !!value && value.length > 0;
}

export function shouldShowAccreditationsField(
  accreditations: string[] | undefined,
  isBeingEdited: boolean,
): boolean {
  if (isBeingEdited) return true;
  else
    return !!accreditations && accreditations.filter((accreditation) => !!accreditation).length > 0;
}

/**
 * Orders the keys of an object in alphabetical order.
 * @param {unknown} unorderedObject - The object with unordered keys.
 * @returns {unknown} - The object with keys ordered alphabetically.
 */
function orderObjectKeys(unorderedObejct: unknown): unknown {
  // eslint-disable-next-line @typescript-eslint/ban-types
  return Object.keys(unorderedObejct as unknown as Object)
    .sort()
    .reduce((obj, key) => {
      // @ts-expect-error this works but typescript complains
      obj[key] = unorderedObejct[key];
      return obj;
    }, {});
}

/**
 * Checks if two objects are equal by stringifying them and comparing the strings.
 * @param {unknown} obj1 - The first object to compare. Type unknown as typescript complains about the type Object
 * @param {unknown} obj2 - The second object to compare. Type unknown as typescript complains about the type Object
 * @returns {boolean} - Returns true if the objects are equal, false otherwise.
 */
export function areEqual(obj1: unknown, obj2: unknown): boolean {
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;

  try {
    return JSON.stringify(orderObjectKeys(obj1)) === JSON.stringify(orderObjectKeys(obj2));
  } catch {
    return false;
  }
}

export function createCardFieldsObject(partialCardFields?: Partial<CardFields>): CardFields {
  return {
    accreditations: undefined,
    addresses: undefined,
    companyLogoImg: undefined,
    coverPhotoImg: undefined,
    department: undefined,
    emails: undefined,
    fieldOrder: [],
    firstName: '',
    headline: undefined,
    honorificPrefix: '',
    honorificSuffix: '',
    jobTitle: undefined,
    lastName: '',
    layout: undefined,
    linkedAttributes: [],
    maidenName: '',
    middleName: '',
    orgName: undefined,
    phoneNumbers: undefined,
    preferredName: '',
    profilePictureImg: { originalImageUrl: undefined, editedImageUrl: undefined },
    pronouns: '',
    urls: undefined,
    ...partialCardFields,
  };
}

export function createCardMetadataObject(
  partialCardMetadata?: Partial<CardMetadata>,
): CardMetadata {
  return {
    activationEmailSentAt: undefined,
    allEmailSignatureFieldsLocked: false,
    allFieldTypesLocked: false,
    bbActivationTimestampMs: undefined,
    blinqMessageEnabled: true,
    cardEditId: '',
    cardId: '',
    cardIsEditable: true,
    cardPresetId: undefined,
    cardTitle: 'Work',
    cardUpdateId: undefined,
    color: '#F45A57',
    colorLocked: false,
    createdBy: undefined,
    creationTimestampMs: 0,
    emailSignature: undefined,
    isInvisible: false,
    isPaused: false,
    layoutImagesLocked: undefined,
    lockedEmailSignatureFields: [],
    lockedFieldTypes: [],
    lockedFields: [],
    logoInQrUrl: undefined,
    logoIsInQr: false,
    orgId: '',
    qrCodeLocked: false,
    recipientEmail: undefined,
    referralLinkEnabled: true,
    referrerCardId: undefined,
    referrerUserId: undefined,
    updatedTimestampMs: 0,
    userId: '',
    userProfileId: undefined,
    whereWeMetEnabled: true,
    ...partialCardMetadata,
  };
}
function mapCardAttributeTypeToCardUrlType(type?: string | null): CardUrl['type'] {
  const urlTypeMap: Record<string, CardUrl['type']> = {
    calendar: 'calendar',
    calendly: 'calendly',
    linkedin: 'linkedin',
    instagram: 'instagram',
    twitter: 'twitter',
    facebook: 'facebook',
    youtube: 'youtube',
    snapchat: 'snapchat',
    whatsapp: 'whatsapp',
    paypal: 'paypal',
    discord: 'discord',
    twitch: 'twitch',
    telegram: 'telegram',
    skype: 'skype',
    wechat: 'wechat',
    tiktok: 'tiktok',
    github: 'github',
    yelp: 'yelp',
    venmo: 'venmo',
    cashapp: 'cashapp',
    signal: 'signal',
    link: 'link',
  };

  const urlType = type && urlTypeMap[type];
  if (urlType) {
    return urlType;
  }
  throw new Error('Invalid URL');
}

/** This a wrapper around the isURLFieldValueValid in card-helpers to allow us to start
 * using some of the new types from the GraphQL schema in the card-helpers.
 */
export function isURLFieldValueValidAdapter(
  userInput?: string | null,
  type?: string | null,
): boolean {
  if (!userInput) {
    throw new Error('Invalid URL');
  }
  const mappedUrlType = mapCardAttributeTypeToCardUrlType(type);
  return isURLFieldValueValid(mappedUrlType, userInput);
}

/** Use this to determine if a card Property/Field can be deleted
 * We can delete when
 * - We're editing a template
 * - We're in the card claim flow and the field is optional
 * - The card item is from a template and is optional
 * - The card item is from the admin and is optional
 * - The value provider is 'member' (user owns their individual data)
 *
 * @param args.isTemplated - Is the card item from a template, i.e. it has a template_id
 * @param args.valueProvider - Who provides the value ('member', 'template', 'admin', 'profile')
 */
export function canDeleteCardItem(args: {
  flavour: CardFlavour | null | undefined;
  isOptional: boolean | null | undefined;
  isTemplated: boolean | null | undefined;
  valueProvider?: string | null | undefined;
}): boolean {
  const { flavour, isOptional, isTemplated, valueProvider } = args;

  if (flavour === CardFlavours.TEMPLATE) {
    return true;
  }

  if (
    flavour === CardFlavours.CLAIM ||
    flavour === CardFlavours.ADD_DETAILS ||
    flavour === CardFlavours.INVITE_TOKEN_CLAIM
  ) {
    // During the claim flows, we allow optional fields to be removed
    return !!isOptional;
  }

  // Allow deletion if value_provider is 'member' - the user owns their individual data
  // and should be able to delete it regardless of template settings
  if (valueProvider === 'member') {
    return true;
  }

  if (isTemplated) {
    // If the attribute is from a template, we should only allow deletion if it's optional
    return !!isOptional;
  }

  if (flavour === CardFlavours.TEAM) {
    // Now we know the attribute isn't from a template, we should allow deletion
    // The admin can delete anything on the card as long as it's not required by a template.
    return true;
  }

  // Now we're just editing a personal card, we should only allow deletion if it's optional
  return !!isOptional;
}
